import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import CountrySelector from '../calculator/CountrySelector';
import countriesByContinent from '../calculator/data/countryData';
import { createEmptyParcel, updateParcelWeights } from '../calculator/utils/calculationUtils';

const Trust: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div
      style={{
        fontSize: 24,
        fontWeight: 600,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </div>
    <div className="muted" style={{ fontSize: 12 }}>
      {label}
    </div>
  </div>
);

type RouteTab = 'parcel' | 'doc';

const TABS: { k: RouteTab; l: string; i: 'box' | 'file' }[] = [
  { k: 'parcel', l: 'Parcel', i: 'box' },
  { k: 'doc', l: 'Document', i: 'file' },
];

type HeroQuoteProps = {
  route: RouteTab;
  setRoute: (r: RouteTab) => void;
  originCountry: string;
  setOriginCountry: (v: string) => void;
  destinationCountry: string;
  setDestinationCountry: (v: string) => void;
  swapLocations: () => void;
  quantity: string;
  setQuantity: (v: string) => void;
  weight: string;
  setWeight: (v: string) => void;
  dimensions: { length: string; width: string; height: string };
  setDimensions: (d: { length: string; width: string; height: string }) => void;
  onSubmit: () => void;
  canSubmit: boolean;
};

const HeroQuote: React.FC<HeroQuoteProps> = ({
  route,
  setRoute,
  originCountry,
  setOriginCountry,
  destinationCountry,
  setDestinationCountry,
  swapLocations,
  quantity,
  setQuantity,
  weight,
  setWeight,
  dimensions,
  setDimensions,
  onSubmit,
  canSubmit,
}) => (
  <div className="card card-pad" style={{ padding: 24, boxShadow: 'var(--shd-3)' }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Quick quote</div>
      <span className="mono" style={{ fontSize: 11, color: 'var(--ink-4)' }}>
        LIVE RATES
      </span>
    </div>

    <div
      style={{
        display: 'flex',
        gap: 4,
        padding: 4,
        background: 'var(--bg-tint)',
        borderRadius: 'var(--r-md)',
        marginBottom: 16,
      }}
    >
      {TABS.map(({ k, l, i }) => (
        <button
          key={k}
          onClick={() => setRoute(k)}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '6px 0',
            borderRadius: 'calc(var(--r-md) - 2px)',
            background: route === k ? 'var(--bg-elev)' : 'transparent',
            color: route === k ? 'var(--ink)' : 'var(--ink-3)',
            boxShadow: route === k ? 'var(--shd-1)' : 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'inherit',
            transition: 'all 0.15s',
          }}
        >
          <Icon name={i} size={14} /> {l}
        </button>
      ))}
    </div>

    <div
      className="hero-route-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: 8,
        alignItems: 'end',
      }}
    >
      <CountrySelector
        label="From"
        value={originCountry}
        onChange={setOriginCountry}
        countries={countriesByContinent}
        purpose="source"
      />
      <button
        type="button"
        onClick={swapLocations}
        title="Swap origin and destination"
        aria-label="Swap countries"
        style={{
          marginBottom: 2,
          width: 36,
          height: 36,
          borderRadius: 'var(--r-md)',
          border: '1px solid var(--line-strong)',
          background: 'var(--bg-elev)',
          color: 'var(--ink-2)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <Icon name="sort" size={16} />
      </button>
      <CountrySelector
        label="To"
        value={destinationCountry}
        onChange={setDestinationCountry}
        countries={countriesByContinent}
        purpose="destination"
      />
    </div>

    {route === 'parcel' ? (
      <div
        className="hero-parcel-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 8,
          marginTop: 12,
        }}
      >
        <div className="field">
          <label className="field-label">Qty</label>
          <input
            className="input"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
          />
        </div>
        <div className="field">
          <label className="field-label">Weight</label>
          <div className="input-group">
            <input
              className="input"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              inputMode="decimal"
            />
            <span className="input-addon">kg</span>
          </div>
        </div>
        {(['length', 'width', 'height'] as const).map((k) => (
          <div className="field" key={k}>
            <label className="field-label">{k[0].toUpperCase() + k.slice(1)}</label>
            <div className="input-group">
              <input
                className="input"
                value={dimensions[k]}
                onChange={(e) =>
                  setDimensions({ ...dimensions, [k]: e.target.value })
                }
                inputMode="decimal"
              />
              <span className="input-addon">cm</span>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 8,
          marginTop: 12,
        }}
      >
        <div className="field">
          <label className="field-label">Weight (max 0.5 kg)</label>
          <div className="input-group">
            <input
              className="input"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              inputMode="decimal"
            />
            <span className="input-addon">kg</span>
          </div>
        </div>
      </div>
    )}

    <button
      onClick={onSubmit}
      disabled={!canSubmit}
      className="btn btn-blue btn-block btn-lg"
      style={{ marginTop: 16, opacity: canSubmit ? 1 : 0.6 }}
    >
      See rates <Icon name="arrowRight" size={16} />
    </button>
    <div className="muted" style={{ fontSize: 12, textAlign: 'center', marginTop: 10 }}>
      Free · Instant · No account needed
    </div>
  </div>
);

const CARRIERS = ['DHL', 'FedEx', 'UPS', 'Aramex', 'SMSA', 'TNT', 'USPS'];

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [route, setRoute] = useState<RouteTab>('parcel');
  const [originCountry, setOriginCountry] = useState<string>('');
  const [destinationCountry, setDestinationCountry] = useState<string>('US');
  const [quantity, setQuantity] = useState<string>('1');
  const [weight, setWeight] = useState<string>('1');
  const [dimensions, setDimensions] = useState({
    length: '10',
    width: '10',
    height: '10',
  });

  const swapLocations = () => {
    const temp = originCountry;
    setOriginCountry(destinationCountry);
    setDestinationCountry(temp);
  };

  const canSubmit = Boolean(originCountry && destinationCountry);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const isDocument = route === 'doc';
    const unitSystem = 'metric' as const;

    const navigationState: any = {
      origin: originCountry,
      destination: destinationCountry,
      shippingType: isDocument ? 'document' : 'package',
      shipmentType:
        originCountry !== destinationCountry ? 'international' : 'domestic',
      unitSystem,
    };

    if (isDocument) {
      navigationState.documents = [
        {
          id: Math.random().toString(36).substring(2, 9),
          quantity: 1,
          weight: Math.min(parseFloat(weight) || 0.3, 0.5),
        },
      ];
    } else {
      const baseParcel = createEmptyParcel(parseFloat(weight) || 1, {
        length: parseFloat(dimensions.length) || 10,
        width: parseFloat(dimensions.width) || 10,
        height: parseFloat(dimensions.height) || 10,
      });
      baseParcel.quantity = Math.max(1, parseInt(quantity, 10) || 1);
      navigationState.parcels = [updateParcelWeights(baseParcel, unitSystem)];
    }

    navigate('/shipping', { state: navigationState });
  };

  return (
    <>
      <section className="hero-grid" style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="page" style={{ paddingTop: 56, paddingBottom: 56 }}>
          <div className="home-hero">
            <div>
              <span className="kicker">
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 99,
                    background: 'var(--brand-mint)',
                    display: 'inline-block',
                    marginRight: 6,
                  }}
                />
                Live · 24 carriers online
              </span>

              <h1
                style={{
                  fontSize: 'clamp(40px, 5.5vw, 72px)',
                  fontWeight: 600,
                  letterSpacing: '-0.035em',
                  lineHeight: 1.02,
                  marginTop: 18,
                }}
              >
                Shipping
                <br />
                <span
                  style={{
                    fontFamily: 'var(--font-disp)',
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: 'var(--brand-blue)',
                  }}
                >
                  infrastructure
                </span>
                <br />
                for the world.
              </h1>

              <p
                style={{
                  fontSize: 18,
                  color: 'var(--ink-2)',
                  maxWidth: 520,
                  marginTop: 20,
                  lineHeight: 1.5,
                }}
              >
                Compare 24+ carriers, book in seconds, and track every shipment from one
                dashboard — with wallet-based pricing that stays predictable.
              </p>

              <div style={{ display: 'flex', gap: 10, marginTop: 28, flexWrap: 'wrap' }}>
                <Button variant="primary" onClick={() => navigate('/shipping')}>
                  <Icon name="package" size={17} /> Ship a package
                </Button>
                <Button variant="secondary" onClick={() => navigate('/track')}>
                  <Icon name="search" size={17} /> Track shipment
                </Button>
              </div>

              <div style={{ display: 'flex', gap: 24, marginTop: 36, flexWrap: 'wrap' }}>
                <Trust label="On-time delivery" value="98.6%" />
                <Trust label="Avg. savings vs. retail" value="32%" />
                <Trust label="Shipments processed" value="2.4M+" />
              </div>
            </div>

            <HeroQuote
              route={route}
              setRoute={setRoute}
              originCountry={originCountry}
              setOriginCountry={setOriginCountry}
              destinationCountry={destinationCountry}
              setDestinationCountry={setDestinationCountry}
              swapLocations={swapLocations}
              quantity={quantity}
              setQuantity={setQuantity}
              weight={weight}
              setWeight={setWeight}
              dimensions={dimensions}
              setDimensions={setDimensions}
              onSubmit={handleSubmit}
              canSubmit={canSubmit}
            />
          </div>
        </div>
      </section>

      <section
        className="page"
        style={{
          paddingTop: 36,
          paddingBottom: 36,
          borderBottom: '1px solid var(--line)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <span className="muted" style={{ fontSize: 13 }}>
            Negotiated rates from
          </span>
          <div
            style={{
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {CARRIERS.map((c) => (
              <span
                key={c}
                className="mono"
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                  color: 'var(--ink-3)',
                  letterSpacing: '0.05em',
                }}
              >
                {c}
              </span>
            ))}
            <Badge>+17 more</Badge>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
